import { GET_PARAMS, MOBILE_WIDTH, GET_ENUMS } from 'const';
import {
  $promotionProduct,
  $timeTo,
  $urgent,
  repeatOrderFx,
  $promoCode,
  $isOrderRepeating,
} from 'domains/cart';
import {
  $type,
  $address,
  $restaurant,
  $deliveryZone,
  $currency,
} from 'domains/cartography';
import { $catalogId } from 'domains/catalog';
import {
  // $isPendingOrders,
  $orders,
  $user,
  // getOrdersList,
  Order,
  ordersPageGate,
  getOrdersFx,
  $orderDetails,
  getOrderByIdFx,
  openRetryPayment,
  closeRetryPayment,
} from 'domains/profile';
import { useGate, useUnit } from 'effector-react';
import { useGetParameter, useLinks } from 'hooks/router';
import { AuthClient } from 'api/base';
import React, { useCallback, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FEBottomSheet, FEDialog, FESpinner } from 'components';
import { useMediaQuery } from 'ui-kit/hooks';
import { Content } from './components';
import style from './style.module.css';
import { useTranslation } from 'react-i18next';
import { OrderDetails } from './components/content/components/order/components';

const OrdersPage = ({ open }: { open: boolean }) => {
  const { t } = useTranslation();
  const isDesktop = useMediaQuery(`(min-width: ${MOBILE_WIDTH}px)`);
  const navigate = useNavigate();
  const location = useLocation();

  const [isPaginationPending, setIsPaginationPending] = useState(false);
  const [lastResponseItemsCount, setLastResponseItemsCount] =
    useState<number>();

  const goBack = () => {
    const isDefault = ((window.history.state && window.history.state.key) || 'default') === 'default';

    if (isDefault) return navigate('/', { replace: true });
    return navigate(-1);
  };

  useGate(ordersPageGate);

  const [
    orders,
    catalogId,
    promoCode,
    promotionProduct,
    type,
    address,
    restaurant,
    user,
    timeTo,
    urgent,
    deliveryZone,
    isOrderRepeating,
    onRepeatOrder,
    currency,
    handleGetOrders,
    orderDetails,
    isOrderDetailsPending,
    handleGetOrderById,
    onOpenRetryPayment,
    onCloseRetryPayment,
  ] = useUnit([
    $orders,
    $catalogId,
    $promoCode,
    $promotionProduct,
    $type,
    $address,
    $restaurant,
    $user,
    $timeTo,
    $urgent,
    $deliveryZone,
    $isOrderRepeating,
    repeatOrderFx,
    $currency,
    getOrdersFx,
    $orderDetails,
    getOrderByIdFx.pending,
    getOrderByIdFx,
    openRetryPayment,
    closeRetryPayment,
  ]);

  const { authLink, cartLink } = useLinks();

  React.useEffect(() => {
    if (AuthClient.isAuth === false) {
      navigate(authLink, { replace: true });
    }
  }, [authLink, navigate]);

  const handleRetryPayment = (order: Order) => {
    onOpenRetryPayment({ order });
    navigate(
      {
        pathname: location.pathname,
        search: `?${GET_PARAMS.page}=${GET_ENUMS.page.cart}&${GET_PARAMS.retryOrderId}=${order.id}`,
      },
      { replace: true },
    );
  };

  const handleRepeatOrder = (order: Order) => {
    onCloseRetryPayment();

    return (
      catalogId &&
      onRepeatOrder({
        order,
        catalogId,
        promoCode,
        promotionProduct,
        type,
        address,
        restaurant,
        user,
        timeTo,
        urgent,
        deliveryZone,
      }).then(() => navigate(cartLink, { replace: true }))
    );
  };

  const paginationPendingRef = React.useRef(false);

  const queryOrderId = useGetParameter(GET_PARAMS.orderId);

  const orderFromList = React.useMemo(() => {
    if (!queryOrderId) {
      return null;
    }

    return (
      orders.find((order) => String(order.id) === String(queryOrderId)) ?? null
    );
  }, [orders, queryOrderId]);

  const orderFromDetails =
    queryOrderId && String(orderDetails?.id) === String(queryOrderId)
      ? orderDetails
      : null;

  const selectedOrder = orderFromList ?? orderFromDetails;

  const isOrderDetailsRequested = Boolean(queryOrderId);

  const isOrderDetailsOpen = Boolean(selectedOrder);

  const loadMoreOrders = React.useCallback(() => {
    if (
      isOrderDetailsOpen ||
      paginationPendingRef.current ||
      lastResponseItemsCount === 0
    ) {
      return;
    }

    paginationPendingRef.current = true;
    setIsPaginationPending(true);

    handleGetOrders({ limit: 15, offset: orders.length })
      .then((res) => {
        setLastResponseItemsCount(res.length);
      })
      .finally(() => {
        paginationPendingRef.current = false;
        setIsPaginationPending(false);
      });
  }, [
    handleGetOrders,
    isOrderDetailsOpen,
    lastResponseItemsCount,
    orders.length,
  ]);

  React.useEffect(() => {
    if (isOrderDetailsOpen) {
      return;
    }

    const handleWindowScroll = () => {
      const scrollTop = window.scrollY;
      const viewportHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;

      const distanceToBottom = documentHeight - scrollTop - viewportHeight;
      const threshold = 600;

      if (distanceToBottom > threshold) {
        return;
      }

      loadMoreOrders();
    };

    window.addEventListener('scroll', handleWindowScroll);

    return () => {
      window.removeEventListener('scroll', handleWindowScroll);
    };
  }, [isOrderDetailsOpen, loadMoreOrders]);

  const closeOrderDetails = React.useCallback(() => {
    const searchParams = new URLSearchParams(location.search);

    searchParams.delete(GET_PARAMS.orderId);

    navigate(
      {
        pathname: location.pathname,
        search: searchParams.toString(),
      },
      {
        replace: true,
      },
    );
  }, [location.pathname, location.search, navigate]);

  React.useEffect(() => {
    if (!queryOrderId) {
      return;
    }

    if (selectedOrder || isOrderDetailsPending) {
      return;
    }

    handleGetOrderById({ id: queryOrderId });
  }, [
    queryOrderId,
    selectedOrder,
    isOrderDetailsPending,
    handleGetOrderById,
  ]);

  const onClose = goBack

  if (isOrderDetailsRequested && !selectedOrder && isOrderDetailsPending) {
    return <FESpinner />;
  }

  if (selectedOrder) {
    return (
      <OrderDetails
        order={selectedOrder}
        currency={currency}
        onClose={closeOrderDetails}
        onRepeatOrder={({ order }) => handleRepeatOrder(order)}
        onRetryPayment={({ order }) => handleRetryPayment(order)}
      />
    );
  }

  return (
    <Content
      currency={currency}
      pendingList={isPaginationPending}
      onClose={onClose}
      orders={orders}
      isPendingOrders={false}
      onRepeatOrder={({ order }) => handleRepeatOrder(order)}
      onRetryPayment={({ order }) => handleRetryPayment(order)}
    />
  );
};

export default OrdersPage;
